<script setup>
import { router } from "@inertiajs/vue3";
import { useModalogStore } from "@/stores/modalog.js";
import { useModalogEscape } from "@/composables/useModalogEscape.js";
import { useWishlistStore } from "@/stores/wishlist.js";
import IconSpoilerAlert from "~/svg/icon-spoiler-alert.svg";

const props = defineProps({
    // Without a list the modalog only flips the spoiled flag in place —
    // the Confirmed Gifts page (GH-923) reveals its grids on confirm
    // instead of navigating to a per-list confirmed view.
    list: {
        type: Object,
        default: null,
    },
});

const modalogStore = useModalogStore();

useModalogEscape();
const wishlistStore = useWishlistStore();

function decline() {
    modalogStore.closeModalog();

    // Deep links land on list.confirmed directly; declining bounces back to
    // the spoiler-free list view.
    if (props.list && route().current() === "list.confirmed") {
        router.get(route("list.show", { giftlist: props.list.uuid }));
    }
}

function confirm() {
    wishlistStore.isSpoiled = true;
    modalogStore.closeModalog();

    if (props.list && route().current() !== "list.confirmed") {
        router.get(route("list.confirmed", { giftlist: props.list.uuid }));
    }
}
</script>

<template>
    <!-- Root stays mounted; overlay toggles on an inner v-if (see
         brand modalog pattern in ManageProfileThemeModalog). -->
    <div>
        <div
            v-if="modalogStore.hasModalog"
            class="brand fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-navy/60 p-4 font-brand text-brand-navy sm:p-8"
            @click.self="decline"
        >
            <div
                class="relative my-auto w-full max-w-[815px] overflow-hidden rounded-[5px] bg-white shadow-brand-hero"
            >
                <header
                    class="flex items-center justify-between px-8 py-6"
                    :style="{
                        backgroundImage:
                            'linear-gradient(99deg, #ff9d3c -10%, #f21439 85%)',
                    }"
                >
                    <h2 class="text-[24px] font-bold text-white">
                        Are you sure?
                    </h2>
                    <button
                        type="button"
                        class="grid size-8 place-items-center text-white transition hover:opacity-75"
                        aria-label="Close"
                        @click="decline"
                    >
                        <svg
                            viewBox="0 0 19 19"
                            class="size-[19px]"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            aria-hidden="true"
                        >
                            <path d="M2 2l15 15M17 2L2 17" />
                        </svg>
                    </button>
                </header>

                <div class="px-8 pb-7 pt-6">
                    <div class="flex items-center gap-6">
                        <IconSpoilerAlert
                            class="size-[50px] shrink-0 sm:size-[67px]"
                            aria-hidden="true"
                        />
                        <p class="text-[20px] leading-[1.4] text-[#102a3d]">
                            <strong
                                >You are about to view gifts that have been
                                confirmed as purchased.</strong
                            ><br />
                            Do you really want to proceed?
                        </p>
                    </div>

                    <div class="mt-8 flex items-center justify-end gap-8">
                        <button
                            type="button"
                            class="font-brand text-[15px] font-bold underline underline-offset-2 transition hover:text-brand-red"
                            @click="decline"
                        >
                            Shoot! No!
                        </button>
                        <button
                            type="button"
                            class="rounded-brand bg-brand-navy px-7 py-3.5 text-[15px] font-bold text-white transition hover:bg-brand-navy/90"
                            @click="confirm"
                        >
                            Yes, Show Me
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
